import React from "react";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";

/**
 * SEO component for managing document head
 */
const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterHandle,
  authorHandle,
  siteTitle,
  children,
}) => {
  const siteName = siteTitle || "MettaStay";

  // Fallbacks
  const metaTitle = title || siteName;
  const metaDescription = description || "";
  const metaOgTitle = ogTitle || metaTitle;
  const metaOgDescription = ogDescription || metaDescription;

  // Structured Data (JSON-LD)
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: siteName,
    url: canonicalUrl || ogUrl || "",
    description: metaDescription,
    image: ogImage || "",
    applicationCategory: "BusinessApplication",
    operatingSystem: "All",
  };

  return (
    <Helmet>
      {/* 🔹 Essential Meta */}
      <title>{metaTitle}</title>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      {metaDescription && <meta name="description" content={metaDescription} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index, follow" />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* 🔹 Favicons & App Icons */}
      <link rel="icon" href="/favicon.ico" type="image/x-icon" />
      <meta name="theme-color" content="#ffffff" />

      {/* 🔹 PWA Manifest */}
      {/* <link rel="manifest" href="/manifest.json" /> */}

      {/* 🔹 Microsoft Tiles */}
      <meta name="msapplication-TileColor" content="#2b5797" />

      {/* 🔹 Open Graph */}
      <meta property="og:type" content="website" />
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta property="og:title" content={metaOgTitle} />
      {metaOgDescription && <meta property="og:description" content={metaOgDescription} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:site_name" content={siteName} />

      {/* 🔹 Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {ogUrl && <meta name="twitter:url" content={ogUrl} />}
      <meta name="twitter:title" content={metaOgTitle} />
      {metaOgDescription && <meta name="twitter:description" content={metaOgDescription} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {twitterHandle && <meta name="twitter:site" content={`@${twitterHandle}`} />}
      {authorHandle && <meta name="twitter:creator" content={`@${authorHandle}`} />}

      {/* 🔹 Structured Data */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      {/* Extra meta from children */}
      {children}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  canonicalUrl: PropTypes.string,
  ogTitle: PropTypes.string,
  ogDescription: PropTypes.string,
  ogImage: PropTypes.string,
  ogUrl: PropTypes.string,
  twitterHandle: PropTypes.string,
  authorHandle: PropTypes.string,
  siteTitle: PropTypes.string,
  children: PropTypes.node,
};

SEO.defaultProps = {
  title: "",
  description: "",
  keywords: "",
  canonicalUrl: "",
  ogTitle: "",
  ogDescription: "",
  ogImage: "",
  ogUrl: "",
  twitterHandle: "",
  authorHandle: "",
  siteTitle: "MettaStay",
  children: null,
};

export default SEO;
